import { LightSdd } from '@/hooks/use-user-trustly-information';

/**
 * The action to take when the user taps Withdraw, resolved from the two Gate-A reasons:
 * - TRUSTLY_ONLY: verification required, satisfiable only by Trustly (no choice screen).
 * - IDPV_ONLY: verification required, only the IDPV path is available (no choice screen).
 * - CHOICE: verification required and both paths are available — show the verification selector.
 * - BLOCKED: verification required, no path is available, and the user has no withdrawal
 *   method on file to fall back to ("verification temporarily unavailable").
 * - CLEARED: no verification required — proceed to the withdrawable-method checks.
 */
export enum WithdrawVerificationOutcome {
    TRUSTLY_ONLY = 'trustlyOnly',
    IDPV_ONLY = 'idpvOnly',
    CHOICE = 'choice',
    BLOCKED = 'blocked',
    CLEARED = 'cleared',
}

type ResolveWithdrawVerificationOutcomeParams = {
    masterEnabled: boolean;
    trustlyPathEnabled: boolean;
    idpvPathEnabled: boolean;
    lightSdd: string | undefined;
    trustlyVerified: boolean;
    idpvCompleted: boolean;
    hasRestrictedBalance: boolean;
    hasWithdrawableMethodOnFile: boolean;
};

export const resolveWithdrawVerificationOutcome = ({
    masterEnabled,
    trustlyPathEnabled,
    idpvPathEnabled,
    lightSdd,
    trustlyVerified,
    idpvCompleted,
    hasRestrictedBalance,
    hasWithdrawableMethodOnFile,
}: ResolveWithdrawVerificationOutcomeParams): WithdrawVerificationOutcome => {
    if (!masterEnabled) {
        return WithdrawVerificationOutcome.CLEARED;
    }

    // Both uses below are reachable only when `trustlyPathEnabled` is false, which usually
    // means a Trustly outage; rather than hard-blocking, users with a method on file fall
    // back to the closed-loop-restricted withdraw flow.
    const noPathFallback = hasWithdrawableMethodOnFile
        ? WithdrawVerificationOutcome.CLEARED
        : WithdrawVerificationOutcome.BLOCKED;

    // Ops-set Light SDD requirement is satisfiable only by Trustly; a prior IDPV does not clear it.
    if (lightSdd === LightSdd.NO_REQUIRED) {
        return trustlyPathEnabled ? WithdrawVerificationOutcome.TRUSTLY_ONLY : noPathFallback;
    }

    // Restricted-balance gate: cleared by either a Trustly or an IDPV verification.
    const restrictedGateFires = hasRestrictedBalance && !trustlyVerified && !idpvCompleted;
    if ((lightSdd === LightSdd.NO || lightSdd === LightSdd.IGNORE) && restrictedGateFires) {
        // LightSdd.IGNORE is the per-user ops override that suppresses the Trustly path.
        const trustlyAvailable = trustlyPathEnabled && lightSdd !== LightSdd.IGNORE;
        if (trustlyAvailable && idpvPathEnabled) {
            return WithdrawVerificationOutcome.CHOICE;
        }
        if (trustlyAvailable) {
            return WithdrawVerificationOutcome.TRUSTLY_ONLY;
        }
        if (idpvPathEnabled) {
            return WithdrawVerificationOutcome.IDPV_ONLY;
        }
        // IGNORE users were never gated before this feature, so fail open for them
        // rather than hard-blocking when the IDPV path is unavailable.
        return lightSdd === LightSdd.IGNORE ? WithdrawVerificationOutcome.CLEARED : noPathFallback;
    }

    return WithdrawVerificationOutcome.CLEARED;
};
